import type { InteractionStep } from "@/services/api/agentic-v2/types";
import { prettifyToolName } from "@/components/missions/task-thread/prettifyToolName";

export interface ToolCallViewProps {
  step: InteractionStep;
  /** Step index within the interaction (for the header). */
  index?: number | null;
}

function formatJson(value: InteractionStep["input"]): string | null {
  if (value == null) return null;
  try {
    const text = JSON.stringify(value, null, 2);
    return text === "{}" ? null : text;
  } catch {
    return String(value);
  }
}

function formatDuration(ms: number | null | undefined): string | null {
  if (ms == null) return null;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/**
 * Staff-only (ops) detail view of a single tool-call step: tool name, inputs,
 * outputs, confidence, disposition, and duration. Reused by the entity preview
 * bubble and the inline tool-name hover bubble in `TaskThread`.
 */
export function ToolCallView({ step, index }: ToolCallViewProps) {
  const label = step.tool_display_name || prettifyToolName(step.tool ?? step.type);
  const input = formatJson(step.input);
  const output = formatJson(step.output);
  const duration = formatDuration(step.duration_ms);

  return (
    <div className="space-y-2 text-left" data-testid="tool-call-view">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-100">{label}</span>
        {index != null ? (
          <span className="text-[10px] uppercase tracking-wide text-slate-500">
            step {index}
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {step.confidence ? (
          <Badge label="Confidence" value={step.confidence} />
        ) : null}
        {step.disposition ? (
          <Badge label="Disposition" value={step.disposition} />
        ) : null}
        {duration ? <Badge label="Duration" value={duration} /> : null}
      </div>

      {step.disposition_reasoning ? (
        <p className="text-xs leading-relaxed text-slate-300">
          {step.disposition_reasoning}
        </p>
      ) : null}

      {input ? <JsonBlock title="Input" body={input} /> : null}
      {output ? <JsonBlock title="Output" body={output} /> : null}

      {!input && !output && !step.disposition_reasoning ? (
        <p className="text-xs text-slate-500">No recorded input or output.</p>
      ) : null}
    </div>
  );
}

function Badge({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-slate-800/80 px-2 py-0.5 text-[11px] text-slate-200">
      <span className="text-slate-400">{label}:</span>
      <span className="font-medium">{value}</span>
    </span>
  );
}

function JsonBlock({ title, body }: { title: string; body: string }) {
  return (
    <div className="space-y-1">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </p>
      <pre className="max-h-48 overflow-auto rounded-lg bg-slate-950/60 p-2 text-[11px] leading-relaxed text-slate-200">
        {body}
      </pre>
    </div>
  );
}
